import { datadogLogs } from '@datadog/browser-logs';
import Api, { Cloud } from '@silbo/core';
import { getMessaging, getToken, onMessage, Unsubscribe } from '@firebase/messaging';
import { useEffect, useState } from 'react';
import { logNotificationReceived } from '../utils/log-notification-action';
import { vapidKey } from './fcm';

type PushNotificationOptions = { title: string } & NotificationOptions;
type ServiceWorkerOptions = { serviceWorkerRegistration?: ServiceWorkerRegistration };

export async function showNotification(
  notificationOptions: PushNotificationOptions,
  serviceWorkerOptions?: ServiceWorkerOptions
) {
  const registration = serviceWorkerOptions?.serviceWorkerRegistration ?? (await getServiceWorkerRegistration());
  logNotificationReceived(notificationOptions, 'foreground');
  return registration.showNotification(notificationOptions.title, notificationOptions);
}

let onMessageUnsubscribe: Unsubscribe | undefined;
export function subscribeToNotifications() {
  if (!onMessageUnsubscribe) {
    onMessageUnsubscribe = onMessage(getMessaging(), (payload) => {
      showNotification({
        title: payload.notification?.title ?? '',
        body: payload.notification?.body,
        icon: './pwa-64x64.png',
        badge: './badge.png',
        silent: false,
      });
    });
  }
}

export function unsubscribeFromNotifications() {
  if (onMessageUnsubscribe) {
    onMessageUnsubscribe();
    onMessageUnsubscribe = undefined;
  }
}

export function useNotificationPermission() {
  const [permission, setPermission] = useState<NotificationPermission | null>(
    isNotificationSupported() ? Notification.permission : null
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isNotificationSupported()) {
      console.warn('Notification API is not supported');
      return;
    }

    navigator.permissions.query({ name: 'notifications' }).then((result) => {
      result.onchange = () => {
        setPermission(Notification.permission);
      };
    });
  }, []);

  const requestPermission = async () => {
    if (!isNotificationSupported()) {
      console.warn('Notification API is not supported');
      return;
    }

    setIsLoading(true);
    setPermission(await Notification.requestPermission());
    setIsLoading(false);
  };

  return { permission, requestPermission, isLoading };
}

export async function retrieveAndStoreFcmToken() {
  const notificationPermission = isNotificationSupported() ? Notification.permission : 'unavailable';
  let sessionInstallationId: string | undefined;
  let localInstallationId: string | undefined;

  try {
    datadogLogs.logger.debug('Starting FCM token retrieval', { notificationPermission });
    const currentSession = await Api.Session.current();

    if (!currentSession) {
      datadogLogs.logger.warn('No current session found during FCM token retrieval', { notificationPermission });
      throw new Error('Session token not found.');
    }

    const installationId = currentSession.get('installationId');
    sessionInstallationId = installationId;
    localInstallationId = await Api._getInstallationId();

    datadogLogs.logger.debug('Session found, getting service worker registration');
    // https://stackoverflow.com/a/66285624
    // https://firebase.google.com/docs/reference/js/messaging_.gettokenoptions.md#gettokenoptionsserviceworkerregistration
    const registration = await getServiceWorkerRegistration();
    datadogLogs.logger.debug('Service worker ready, getting FCM token');

    const fcmToken = await getToken(getMessaging(), { vapidKey, serviceWorkerRegistration: registration });
    datadogLogs.logger.debug('FCM token retrieved successfully');

    datadogLogs.logger.debug('Updating installation with FCM token', {
      installationId: sessionInstallationId ? 'present' : 'missing',
    });

    await Cloud.updateInstallation({
      installationId,
      deviceType: 'WEB',
      deviceToken: fcmToken,
    });
    datadogLogs.logger.info('FCM token stored successfully', {
      notificationPermission,
      sessionInstallationId,
      localInstallationId,
      installationIdsMatch: sessionInstallationId === localInstallationId,
      fcmTokenSuffix: fcmToken.slice(-8),
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Error retrieving and storing FCM token', error);
    datadogLogs.logger.error(
      'Error retrieving and storing FCM token',
      {
        errorMessage: error?.message,
        errorType: error?.constructor?.name,
        hasSession: !!(await Api.Session.current().catch(() => null)),
        notificationPermission,
        sessionInstallationId,
        localInstallationId,
      },
      error
    );
  }
}

export function isNotificationSupported() {
  return 'Notification' in window;
}

async function getServiceWorkerRegistration() {
  return navigator.serviceWorker.ready;
}
